import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBag } from '../context/BagContext';
import { useTracking } from '../context/TrackingContext';
import { useAddress } from '../context/AddressContext';
import { usePushNotifications } from '../context/PushNotificationContext';
import { useLikedItems } from '../context/WishlistContext';
import SuccessModal from '../components/SuccessModal';
import AccountInformationSection from '../components/AccountInformationSection';
import AddressFormModal from '../components/AddressFormModal';
import SiteLoading from '../components/SiteLoading';
import { userService } from '../services/api';
import useSiteBranding from '../hooks/useSiteBranding';
import usePublicContactDisplay from '../hooks/usePublicContactDisplay';
import { getTelHrefFromDisplayPhone } from '../utils/publicContactInfo';
import {
  parseContactChannelsFromStorage,
  normalizeContactChannelsData,
  buildWhatsAppUrl,
  getWhatsAppOrderPhoneDigits,
  getWhatsAppChatHelpPhoneDigits,
} from '../utils/whatsappContact';
import {
  getSavedCouponCodes,
  addSavedCouponCode,
  removeSavedCouponCode,
  normalizeSavedCouponCode,
} from '../utils/savedCouponsStorage';
import './ProfilePage.css';

const RECENT_VISITS_PAGE_SIZE = 10;
const RECENT_VISITS_LIST_LIMIT = 50;

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const { darkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToBag } = useBag();
  const { getRecentVisits } = useTracking();
  const { getUserAddresses, addAddress, updateAddress, deleteAddress, setDefaultAddress } = useAddress();
  const {
    prefs: notificationPrefs,
    updatePrefs: updateNotificationPrefs,
    clearUnread: clearNotificationUnread,
  } = usePushNotifications();
  const { likedItems, removeFromLikedItems } = useLikedItems();
  const { siteName } = useSiteBranding();
  const { phoneDisplay, email: publicContactEmail } = usePublicContactDisplay();
  const brand = siteName || 'Sheen Wear';
  const publicTelHref = getTelHrefFromDisplayPhone(phoneDisplay);

  // State for data
  const [activeSection, setActiveSection] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const recentVisitsSectionRef = useRef(null);
  const pendingRecentVisitsScrollRef = useRef(false);
  const [storeContact, setStoreContact] = useState(() =>
    normalizeContactChannelsData(parseContactChannelsFromStorage() || {})
  );

  // Get user data
  const userAddresses = user ? getUserAddresses(user.id) : [];
  // Sort addresses: default address first, then others
  const addresses = [...userAddresses].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return 0;
  });
  const recentVisits = getRecentVisits(RECENT_VISITS_LIST_LIMIT);
  const [recentVisitsPage, setRecentVisitsPage] = useState(1);
  const [storeCredit] = useState(0.00);

  const userIdForCoupons = user?.id ?? user?._id;
  const [savedCouponsProfile, setSavedCouponsProfile] = useState([]);
  const [newCouponToSave, setNewCouponToSave] = useState('');

  useEffect(() => {
    const sync = () => {
      setSavedCouponsProfile(userIdForCoupons ? getSavedCouponCodes(userIdForCoupons) : []);
    };
    sync();
    window.addEventListener('sheenwearSavedCouponsUpdated', sync);
    return () => window.removeEventListener('sheenwearSavedCouponsUpdated', sync);
  }, [userIdForCoupons]);

  const recentVisitsTotalPages = Math.max(
    1,
    Math.ceil(recentVisits.length / RECENT_VISITS_PAGE_SIZE)
  );
  const recentVisitsPageSafe = Math.min(recentVisitsPage, recentVisitsTotalPages);
  const recentVisitsPageItems = recentVisits.slice(
    (recentVisitsPageSafe - 1) * RECENT_VISITS_PAGE_SIZE,
    recentVisitsPageSafe * RECENT_VISITS_PAGE_SIZE
  );

  useEffect(() => {
    setRecentVisitsPage((p) => (p > recentVisitsTotalPages ? recentVisitsTotalPages : p));
  }, [recentVisitsTotalPages]);

  useEffect(() => {
    const syncContact = () => {
      const channels = parseContactChannelsFromStorage();
      if (channels) setStoreContact(normalizeContactChannelsData(channels));
    };
    syncContact();
    window.addEventListener('storage', syncContact);
    return () => window.removeEventListener('storage', syncContact);
  }, []);

  const couponChatUrl = React.useMemo(() => {
    const chatPhone =
      storeContact.whatsappChatNumber || storeContact.whatsappOrderNumber || '';
    const digitsFallback = getWhatsAppOrderPhoneDigits();
    let url = buildWhatsAppUrl(chatPhone, 'Hi! I need a coupon code for my order.');
    if (!url) {
      url = buildWhatsAppUrl(digitsFallback, 'Hi! I need a coupon code for my order.');
    }
    return url;
  }, [storeContact]);

  const openProfileSupportWhatsApp = useCallback(() => {
    const digits = getWhatsAppChatHelpPhoneDigits();
    if (!digits) {
      window.alert(
        'WhatsApp number is not set yet. Please call or email us using the details above.'
      );
      return;
    }
    const text = [
      `Message from ${brand} website`,
      '',
      `Name: ${(user?.name || '').trim() || '(not provided)'}`,
      `Email: ${(user?.email || '').trim() || '(not provided)'}`,
      `Phone: ${(user?.phone || '').trim() || '(not provided)'}`,
      '',
      'I need help (opened from my profile).',
    ].join('\n');
    const url = buildWhatsAppUrl(digits, text);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, [user, brand]);

  const loadUserOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!token) {
        setOrders([]);
        return;
      }

      const data = await userService.getOrders();
      if (data?.success) {
        const list = data.orders ?? data.data ?? [];
        setOrders(Array.isArray(list) ? list : []);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load user orders
  useEffect(() => {
    if (user) {
      loadUserOrders();
    } else {
      setOrders([]);
      setIsLoading(false);
    }
  }, [user, loadUserOrders]);

  const PROFILE_LINK_SECTIONS = React.useMemo(
    () =>
      new Set([
        'profile',
        'addresses',
        'account',
        'security',
        'notifications',
        'wishlist',
        'coupons',
        'support',
      ]),
    []
  );

  // Open correct tab from header / notification links (e.g. { state: { section: 'notifications' } })
  useEffect(() => {
    const section = location.state?.section;
    if (!section) return;
    if (section === 'orders') {
      navigate('/orders', { replace: true, state: {} });
      return;
    }
    if (PROFILE_LINK_SECTIONS.has(section)) {
      setActiveSection(section);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate, PROFILE_LINK_SECTIONS]);

  useEffect(() => {
    if (activeSection !== 'profile' || !pendingRecentVisitsScrollRef.current) return;
    pendingRecentVisitsScrollRef.current = false;
    const t = window.setTimeout(() => {
      recentVisitsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'notifications') {
      clearNotificationUnread();
    }
  }, [activeSection, clearNotificationUnread]);

  useEffect(() => {
    if (activeSection === 'privacy') {
      setActiveSection('profile');
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'recommendations') {
      setActiveSection('profile');
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'cases') {
      setActiveSection('support');
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'preferences') {
      setActiveSection('account');
    }
  }, [activeSection]);

  const sidebarItems = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'addresses', label: 'Shipping Address Book', icon: '📍' },
    { id: 'orders', label: 'Order History', icon: '📋' },
    { id: 'wishlist', label: 'Wishlist', icon: '❤️' },
    { id: 'account', label: 'Account', icon: '👤' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'coupons', label: 'Coupons', icon: '🎫' },
    { id: 'support', label: 'Support', icon: '🆘' },
    { id: 'signout', label: 'Sign Out', icon: '🚪' }
  ];

  const handleSidebarClick = (itemId) => {
    if (itemId === 'signout') {
      if (logout) {
        logout();
        navigate('/');
      }
      return;
    }
    if (itemId === 'orders') {
      navigate('/orders');
      return;
    }
    setActiveSection(itemId);
  };

  const handleProfileStatOrders = () => {
    setIsMobileMenuOpen(false);
    navigate('/orders');
  };

  const handleProfileStatRecentVisits = () => {
    setIsMobileMenuOpen(false);
    if (activeSection !== 'profile') {
      pendingRecentVisitsScrollRef.current = true;
      setActiveSection('profile');
      return;
    }
    recentVisitsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleProfileStatAddresses = () => {
    setActiveSection('addresses');
    setIsMobileMenuOpen(false);
  };

  const handleProfileStatWishlist = () => {
    setActiveSection('wishlist');
    setIsMobileMenuOpen(false);
  };

  const handleAddToCartFromRecommendations = (item) => {
    addToBag(item);
    setSuccessMessage('🛒 Item added to cart successfully! Great choice!');
    setShowSuccessModal(true);
  };

  const formatPrice = (price) => {
    if (!price || typeof price !== 'number') {
      return 'Rs. 0';
    }
    
    try {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0
      }).format(price);
    } catch (error) {
      console.error('Error formatting price:', error);
      return 'Rs. 0';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    
    try {
      return new Date(dateString).toLocaleDateString('en-PK', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const getOrderStatusClassKey = (status) => {
    if (!status) return 'unknown';
    const k = String(status).toLowerCase().trim();
    if (k === 'canceled' || k === 'cancelled') return 'cancelled';
    if (k === 'delivered') return 'delivered';
    if (k === 'shipped' || k === 'dispatched') return 'shipped';
    if (k === 'processing' || k === 'pending' || k === 'confirmed') return 'processing';
    if (k.includes('cancel')) return 'cancelled';
    if (k.includes('deliver')) return 'delivered';
    if (k.includes('ship') || k.includes('dispatch')) return 'shipped';
    if (k.includes('process') || k.includes('pending')) return 'processing';
    return 'unknown';
  };

  const formatOrderStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return String(status)
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="cv-profile-section">
            {/* Profile Header */}
            <div className="profile-header">
              <div className="profile-info">
                <h1 className="profile-name">{user?.name || 'User Name'}</h1>
                <p className="profile-email">{user?.email || 'user@email.com'}</p>
                <p className="profile-phone">{user?.phone || 'Phone Number'}</p>
                <div className="profile-stats">
                  <button
                    type="button"
                    className="stat-item stat-item--action"
                    onClick={handleProfileStatOrders}
                    aria-label="Open order history"
                  >
                    <span className="stat-number">{orders.length}</span>
                    <span className="stat-label">Orders</span>
                  </button>
                  <button
                    type="button"
                    className="stat-item stat-item--action"
                    onClick={handleProfileStatRecentVisits}
                    aria-label="Scroll to recent visits"
                  >
                    <span className="stat-number">{recentVisits.length}</span>
                    <span className="stat-label">Recent Visits</span>
                  </button>
                  <button
                    type="button"
                    className="stat-item stat-item--action"
                    onClick={handleProfileStatWishlist}
                    aria-label="Open wishlist"
                  >
                    <span className="stat-number">{likedItems.length}</span>
                    <span className="stat-label">Wishlist</span>
                  </button>
                  <button
                    type="button"
                    className="stat-item stat-item--action"
                    onClick={handleProfileStatAddresses}
                    aria-label="Open shipping address book"
                  >
                    <span className="stat-number">{addresses.length}</span>
                    <span className="stat-label">Addresses</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="cv-section cv-section--recent-orders">
              <div className="recent-orders-header">
                <h2 className="section-title section-title--compact">Recent orders</h2>
                <p className="recent-orders-lede">Your latest purchases. Open a card to view full history.</p>
              </div>
              {isLoading ? (
                <SiteLoading
                  title="Loading Your Orders"
                  subtitle="Fetching order history..."
                  icon="📋"
                  variant="inline"
                />
              ) : orders.length === 0 ? (
                <div className="empty-state empty-state--orders">
                  <div className="empty-icon" aria-hidden>📦</div>
                  <p>No orders yet</p>
                  <small>When you shop with us, your orders will show up here.</small>
                </div>
              ) : (
                <div className="orders-list orders-list--grid">
                  {orders.slice(0, 5).map((order) => {
                    const statusKey = getOrderStatusClassKey(order.status);
                    const oid = order.orderNumber || order._id || order.id || '—';
                    return (
                      <button
                        key={order._id || order.id}
                        type="button"
                        className={`order-card order-card--profile order-card--status-${statusKey}`}
                        onClick={() => navigate('/orders')}
                      >
                        <span className="order-card__accent" aria-hidden />
                        <div className="order-card__top">
                          <div className="order-card__id-wrap">
                            <span className="order-card__eyebrow">Order</span>
                            <span className="order-card__id">#{oid}</span>
                          </div>
                          <span className={`order-card__pill order-card__pill--${statusKey}`}>
                            {formatOrderStatusLabel(order.status)}
                          </span>
                        </div>
                        <div className="order-card__meta">
                          <div className="order-card__meta-block">
                            <span className="order-card__meta-key">Placed</span>
                            <span className="order-card__meta-val">
                              {formatDate(order.createdAt || order.date)}
                            </span>
                          </div>
                          <div className="order-card__meta-block order-card__meta-block--total">
                            <span className="order-card__meta-key">Total</span>
                            <span className="order-card__meta-val order-card__price">
                              {formatPrice(order.totalAmount || order.total)}
                            </span>
                          </div>
                        </div>
                        {Array.isArray(order.items) && order.items.length > 0 ? (
                          <div className="order-card__lines">
                            <span className="order-card__lines-hdr">
                              {order.items.length} line{order.items.length !== 1 ? 's' : ''}
                            </span>
                            <div className="order-card__chips">
                              {order.items.slice(0, 3).map((item, index) => (
                                <span key={index} className="order-card__chip">
                                  <span className="order-card__chip-name">
                                    {item.productName || item.name || 'Item'}
                                  </span>
                                  <span className="order-card__chip-qty">×{item.quantity ?? 1}</span>
                                </span>
                              ))}
                              {order.items.length > 3 && (
                                <span className="order-card__chip order-card__chip--more">
                                  +{order.items.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        ) : null}
                        <div className="order-card__foot">
                          <span className="order-card__cta">View order history</span>
                          <span className="order-card__chev" aria-hidden>
                            →
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>


            {/* Recent Visits */}
            <div
              className="cv-section"
              id="profile-recent-visits"
              ref={recentVisitsSectionRef}
            >
              <h2 className="section-title">👀 Recent Visits ({recentVisits.length})</h2>
              {recentVisits.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">👀</div>
                  <p>No recent visits</p>
                  <small>Browse products to see your visit history</small>
                </div>
              ) : (
                <>
                  <div className="visits-grid">
                    {recentVisitsPageItems.map(item => (
                      <div key={item.id} className="visit-item">
                        <div className="item-image">
                          <img src={item.image} alt={item.name} />
                          {item.isNew && <span className="badge badge-new">New</span>}
                          {item.isOnSale && <span className="badge badge-sale">Sale</span>}
                        </div>
                        <div className="item-details">
                          <div className="item-category">
                            {item.subcategory ? item.subcategory.toUpperCase() : (item.category || 'General').toUpperCase()}
                          </div>
                          <h4 className="item-name">{item.name}</h4>
                          <div className="item-price">
                            <span className="current-price">{formatPrice(item.price)}</span>
                            {item.originalPrice && item.originalPrice > item.price && (
                              <span className="original-price">{formatPrice(item.originalPrice)}</span>
                            )}
                          </div>
                          <div className="item-visited">
                            Visited {item.visits || 0} time{(item.visits || 0) > 1 ? 's' : ''} • 
                            Last visit: {item.visitedAt ? new Date(item.visitedAt).toLocaleDateString() : 'Unknown Date'}
                          </div>
                        </div>
                        <div className="item-actions">
                          <button 
                            className="btn-primary add-to-cart"
                            onClick={() => handleAddToCartFromRecommendations(item)}
                            title="Add to Bag"
                          >
                            <span className="btn-icon">🛍️</span>
                            Add to Bag
                          </button>
                          <button 
                            className="btn-secondary view-product"
                            onClick={() => navigate(`/product/${item.id}`)}
                            title="View Product"
                          >
                            <span className="btn-icon">👁️</span>
                            View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {recentVisitsTotalPages > 1 && (
                    <div className="visits-pagination" role="navigation" aria-label="Recent visits pages">
                      <button
                        type="button"
                        className="visits-pagination-btn"
                        disabled={recentVisitsPageSafe <= 1}
                        onClick={() => setRecentVisitsPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </button>
                      <span className="visits-pagination-status">
                        Page {recentVisitsPageSafe} of {recentVisitsTotalPages}
                      </span>
                      <button
                        type="button"
                        className="visits-pagination-btn"
                        disabled={recentVisitsPageSafe >= recentVisitsTotalPages}
                        onClick={() =>
                          setRecentVisitsPage((p) => Math.min(recentVisitsTotalPages, p + 1))
                        }
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );

      case 'addresses':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>SHIPPING ADDRESS BOOK ({addresses.length})</h2>
              <button 
                className="btn-add-address"
                onClick={() => {
                  setEditingAddress(null);
                  setShowAddressModal(true);
                }}
              >
                + Add New Address
              </button>
            </div>
            
            {addresses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📍</div>
                <p>No addresses found</p>
                <small>Add your first shipping address to get started</small>
                <button 
                  className="btn-primary"
                  onClick={() => {
                    setEditingAddress(null);
                    setShowAddressModal(true);
                  }}
                  style={{ marginTop: '16px' }}
                >
                  + Add Address
                </button>
              </div>
            ) : (
              <div className="addresses-list">
                {addresses.map(addr => (
                  <div key={addr.id} className="address-card">
                    <div className="address-header">
                      <div>
                        <h3>{addr.title || (addr.type === 'home' ? 'Home' : addr.type === 'work' ? 'Work' : 'Other')}</h3>
                        {addr.isDefault && <span className="default-badge">Default</span>}
                      </div>
                      <div className="address-actions">
                        {!addr.isDefault && (
                          <button
                            className="btn-set-default"
                            onClick={async () => {
                              try {
                                await setDefaultAddress(addr.id);
                                setSuccessMessage('✅ Default address updated successfully!');
                                setShowSuccessModal(true);
                              } catch (error) {
                                alert('Failed to set default address: ' + error.message);
                              }
                            }}
                            title="Set as default"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          className="btn-edit-address"
                          onClick={() => {
                            setEditingAddress(addr);
                            setShowAddressModal(true);
                          }}
                          title="Edit address"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn-delete-address"
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this address?')) {
                              try {
                                await deleteAddress(addr.id);
                                setSuccessMessage('✅ Address deleted successfully!');
                                setShowSuccessModal(true);
                              } catch (error) {
                                alert('Failed to delete address: ' + error.message);
                              }
                            }
                          }}
                          title="Delete address"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                    <div className="address-details">
                      <p><strong>{addr.name || user?.name || ''}</strong></p>
                      <p>{addr.addressLine1 || addr.address}</p>
                      {addr.addressLine2 && <p>{addr.addressLine2}</p>}
                      <p>{addr.city}{addr.state && `, ${addr.state}`}{addr.postalCode && ` ${addr.postalCode}`}</p>
                      {addr.country && <p>{addr.country}</p>}
                      {addr.phone && <p>Phone: {addr.phone}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <AddressFormModal
              isOpen={showAddressModal}
              onClose={() => {
                setShowAddressModal(false);
                setEditingAddress(null);
              }}
              onSave={async (addressData) => {
                try {
                  if (editingAddress) {
                    await updateAddress(editingAddress.id, addressData);
                    setSuccessMessage('✅ Address updated successfully!');
                  } else {
                    await addAddress(addressData);
                    setSuccessMessage('✅ Address added successfully!');
                  }
                  setShowSuccessModal(true);
                  setShowAddressModal(false);
                  setEditingAddress(null);
                } catch (error) {
                  throw error;
                }
              }}
              address={editingAddress}
              user={user}
              addressesCount={addresses.length}
            />
          </div>
        );

      case 'wishlist':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>WISHLIST ({likedItems.length})</h2>
            </div>
            {likedItems.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">❤️</div>
                <p>Your wishlist is empty</p>
                <small>Save items you love from a product page using the heart on the image</small>
              </div>
            ) : (
              <div className="visits-grid">
                {likedItems.map((item) => (
                  <div key={item.id} className="visit-item">
                    <div className="item-image">
                      <img
                        src={item.image || `${process.env.PUBLIC_URL || ''}/logo192.png`}
                        alt={item.name}
                        onError={(e) => {
                          e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/logo192.png`;
                        }}
                      />
                      {item.isNew && <span className="badge badge-new">New</span>}
                      {item.isOnSale && <span className="badge badge-sale">Sale</span>}
                    </div>
                    <div className="item-details">
                      <h4 className="item-name">{item.name}</h4>
                      <div className="item-price">
                        <span className="current-price">{formatPrice(item.price)}</span>
                        {item.originalPrice && item.originalPrice > item.price && (
                          <span className="original-price">{formatPrice(item.originalPrice)}</span>
                        )}
                      </div>
                    </div>
                    <div className="item-actions">
                      <button
                        type="button"
                        className="btn-secondary view-product"
                        onClick={() =>
                          navigate(item.url ? `/${String(item.url).replace(/^\//, '')}` : `/product/${item.id}`)
                        }
                        title="View product"
                      >
                        <span className="btn-icon">👁️</span>
                        View
                      </button>
                      <button
                        type="button"
                        className="btn-primary add-to-cart"
                        onClick={() => removeFromLikedItems(item.id)}
                        title="Remove from wishlist"
                      >
                        <span className="btn-icon">❤️</span>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'account':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>👤 Account Settings</h2>
            </div>
            <div className="cv-section account-settings-cv-section">
              <h3 className="section-subtitle">Account Information</h3>
              <AccountInformationSection className="profile-account-info" />
            </div>

            <div className="cv-section">
              <h3 className="section-subtitle">Language &amp; Region</h3>
              <div className="info-row preferences-language-region-fields">
                <div className="info-item">
                  <label htmlFor="pref-language">Language</label>
                  <select id="pref-language" defaultValue="en">
                    <option value="en">English</option>
                    <option value="ur">Urdu</option>
                  </select>
                </div>
                <div className="info-item">
                  <label htmlFor="pref-currency">Currency</label>
                  <select id="pref-currency" defaultValue="PKR">
                    <option value="PKR">PKR (₨)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
                <div className="info-item">
                  <label htmlFor="pref-timezone">Time Zone</label>
                  <select id="pref-timezone" defaultValue="PKT">
                    <option value="PKT">Pakistan Standard Time (PKT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="cv-section">
              <h3 className="section-subtitle">Display Preferences</h3>
              <div className="preference-item">
                <div className="preference-info">
                  <p><strong>Dark Mode</strong></p>
                  <small>Switch to dark theme across the store</small>
                </div>
                <label className="toggle-switch" htmlFor="pref-dark-mode">
                  <input
                    id="pref-dark-mode"
                    type="checkbox"
                    checked={darkMode}
                    onChange={(e) => setDarkMode(e.target.checked)}
                    aria-label="Enable dark mode"
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
            <div className="section-actions">
              <button type="button" className="btn-primary">
                Save Preferences
              </button>
              <button type="button" className="btn-secondary">
                Reset to Defaults
              </button>
            </div>

            <div className="cv-section cv-section--account-optional">
              <details className="account-data-management-details">
                <summary
                  className="account-data-management-summary"
                  aria-label="Data management, optional section"
                >
                  <span className="account-data-management-title">
                    <span className="account-data-management-icon" aria-hidden>📁</span>
                    Data management
                  </span>
                  <span className="account-optional-pill">Optional</span>
                </summary>
                <p className="account-data-management-lead">
                  Export a copy of your data or request removal. You can open this section only when you need it.
                </p>
                <div className="privacy-actions account-data-management-actions">
                  <button type="button" className="btn-secondary">
                    Download My Data
                  </button>
                  <button type="button" className="btn-danger">
                    Delete All Data
                  </button>
                  <p className="help-text">
                    ⚠️ Deleting your data will permanently remove all your information from our servers.
                  </p>
                </div>
              </details>
            </div>

            <div className="cv-section">
              <h3 className="section-subtitle">Account Management</h3>
              <div className="account-actions">
                <button className="btn-danger">Delete Account</button>
                <p className="help-text">⚠️ This action cannot be undone. All your data will be permanently deleted.</p>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>🔒 Security Settings</h2>
            </div>
            <div className="cv-section">
              <h3 className="section-subtitle">Password</h3>
              <div className="info-row password-change-fields">
                <div className="info-item password-change-field">
                  <label htmlFor="security-current-password">Current Password</label>
                  <input
                    id="security-current-password"
                    type="password"
                    placeholder="Enter current password"
                    autoComplete="current-password"
                  />
                </div>
                <div className="info-item password-change-field">
                  <label htmlFor="security-new-password">New Password</label>
                  <input
                    id="security-new-password"
                    type="password"
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                </div>
                <div className="info-item password-change-field">
                  <label htmlFor="security-confirm-password">Confirm New Password</label>
                  <input
                    id="security-confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="section-actions">
                <button className="btn-primary">Update Password</button>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>🔔 Notification Preferences</h2>
            </div>
            <div className="cv-section">
              <h3 className="section-subtitle">Email Notifications</h3>
              <p className="notification-channel-hint">
                Email options are saved here for when we send order and marketing messages from the store.
                Promotional pop-ups on the site use the Promotional Emails toggle for vibration, tab badge, and
                the same style of timed close.
              </p>
              <div className="notification-item">
                <div className="notification-info">
                  <p><strong>Order Updates</strong></p>
                  <small>Receive emails about your order status</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.emailOrderUpdates}
                    onChange={(e) =>
                      updateNotificationPrefs({ emailOrderUpdates: e.target.checked })
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="notification-item">
                <div className="notification-info">
                  <p><strong>Promotional Emails</strong></p>
                  <small>Receive special offers and promotions</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.emailPromotional}
                    onChange={(e) =>
                      updateNotificationPrefs({ emailPromotional: e.target.checked })
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="notification-item">
                <div className="notification-info">
                  <p><strong>Newsletter</strong></p>
                  <small>Get updates about new products and features</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.emailNewsletter}
                    onChange={(e) =>
                      updateNotificationPrefs({ emailNewsletter: e.target.checked })
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="cv-section">
              <h3 className="section-subtitle">Push Notifications</h3>
              <p className="notification-channel-hint">
                In-app alerts show a centered message for 5 seconds, update your tab title and favicon count,
                set the installable app badge when supported, and vibrate once on phones. Order placement can
                trigger an alert when Order Alerts is on.
              </p>
              <div className="notification-item">
                <div className="notification-info">
                  <p><strong>Order Alerts</strong></p>
                  <small>Get notified when your order status changes</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.pushOrderAlerts}
                    onChange={(e) =>
                      updateNotificationPrefs({ pushOrderAlerts: e.target.checked })
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="notification-item">
                <div className="notification-info">
                  <p><strong>Price Drops</strong></p>
                  <small>Be notified when items in your wishlist go on sale</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.pushPriceDrops}
                    onChange={(e) =>
                      updateNotificationPrefs({ pushPriceDrops: e.target.checked })
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="section-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  if (typeof window.showToast === 'function') {
                    window.showToast('Notification preferences saved.', 'success', 3000);
                  }
                }}
              >
                Save Preferences
              </button>
            </div>
          </div>
        );

      case 'coupons':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>COUPONS</h2>
            </div>
            <p className="profile-coupons-intro">
              Save codes you receive here. On checkout, open Promo Code — your saved codes appear
              there; they only apply when you tap <strong>Apply</strong>.
            </p>
            <div className="profile-coupon-save-row">
              <input
                type="text"
                className="profile-coupon-save-input"
                placeholder="Enter coupon code"
                value={newCouponToSave}
                onChange={(e) => setNewCouponToSave(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const c = normalizeSavedCouponCode(newCouponToSave);
                    if (!c || !userIdForCoupons) return;
                    addSavedCouponCode(userIdForCoupons, c);
                    setNewCouponToSave('');
                    if (typeof window.showToast === 'function') {
                      window.showToast('Coupon saved. Use it on checkout.', 'success', 3000);
                    }
                  }
                }}
                autoComplete="off"
              />
              <button
                type="button"
                className="btn-primary profile-coupon-save-btn"
                onClick={() => {
                  const c = normalizeSavedCouponCode(newCouponToSave);
                  if (!c || !userIdForCoupons) return;
                  addSavedCouponCode(userIdForCoupons, c);
                  setNewCouponToSave('');
                  if (typeof window.showToast === 'function') {
                    window.showToast('Coupon saved. Use it on checkout.', 'success', 3000);
                  }
                }}
              >
                Save code
              </button>
            </div>
            {savedCouponsProfile.length > 0 ? (
              <ul className="profile-saved-coupons-list">
                {savedCouponsProfile.map((code) => (
                  <li key={code} className="profile-saved-coupon-item">
                    <span className="profile-saved-coupon-code">{code}</span>
                    <button
                      type="button"
                      className="profile-saved-coupon-remove"
                      onClick={() => {
                        if (!userIdForCoupons) return;
                        removeSavedCouponCode(userIdForCoupons, code);
                        if (typeof window.showToast === 'function') {
                          window.showToast('Removed from saved codes.', 'info', 2500);
                        }
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="profile-coupons-empty-hint">
                <p>No saved codes yet. Add one above when you receive a coupon.</p>
              </div>
            )}
            {couponChatUrl && (
              <div className="profile-coupons-wa-wrap">
                <a
                  href={couponChatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary profile-coupons-wa-btn"
                >
                  <i className="fab fa-whatsapp" />
                  Get a coupon via WhatsApp
                </a>
              </div>
            )}
          </div>
        );

      case 'support':
        return (
          <div className="section-content">
            <div className="section-header">
              <h2>SUPPORT</h2>
            </div>
            <div className="support-info">
              <div className="support-card">
                <h3>Contact Support</h3>
                <p>Need help? Our support team is here for you.</p>
                <div className="contact-details">
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${publicContactEmail}`}>{publicContactEmail}</a>
                  </p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    <a href={publicTelHref}>{phoneDisplay}</a>
                  </p>
                  <p>
                    <strong>Hours:</strong> 9 AM - 6 PM (Mon-Fri)
                  </p>
                </div>
                <button
                  type="button"
                  className="support-wa-btn"
                  onClick={openProfileSupportWhatsApp}
                >
                  <i className="fab fa-whatsapp" aria-hidden />
                  Contact via WhatsApp
                </button>
                <p className="support-wa-hint">
                  Opens WhatsApp with the same number as Contact Us and the site chat, with your
                  profile name, email, and phone prefilled. Add your order number in the message if
                  needed.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="auth-required">
          <div className="auth-container">
            <div className="auth-illustration">
              <div className="auth-icon">🔐</div>
              <div className="auth-decoration">
                <div className="decoration-circle circle-1"></div>
                <div className="decoration-circle circle-2"></div>
                <div className="decoration-circle circle-3"></div>
              </div>
            </div>
            
            <div className="auth-content">
              <h2>Welcome Back!</h2>
              <p className="auth-message">
                To access your personalized profile dashboard and manage your account settings, 
                please sign in to your account or create a new one.
              </p>
              
              <div className="auth-features">
                <div className="feature-item">
                  <span className="feature-icon">👤</span>
                  <span>Manage Profile & Settings</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📦</span>
                  <span>Track Orders & History</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📍</span>
                  <span>Manage Addresses</span>
                </div>
              </div>
            </div>
            
            <div className="auth-actions">
              <button 
                className="login-btn primary"
                onClick={() => navigate('/login')}
              >
                <span className="btn-icon">🔑</span>
                <span className="btn-text">Login Again</span>
              </button>
              <button 
                className="login-btn secondary"
                onClick={() => navigate('/signup')}
              >
                <span className="btn-icon">✨</span>
                <span className="btn-text">Create Account</span>
              </button>
            </div>
            
            <div className="auth-footer">
              <p>New to Super Wear? Join our community today!</p>
              <div className="auth-benefits">
                <span>🚀</span>
                <span>Exclusive Offers</span>
                <span>•</span>
                <span>🎁</span>
                <span>Welcome Bonus</span>
                <span>•</span>
                <span>⭐</span>
                <span>Premium Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message={successMessage}
          customActions={
            <button 
              className="success-btn primary"
              onClick={() => setShowSuccessModal(false)}
            >
              <i className="fas fa-check"></i>
              Got it!
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Sidebar */}
        <div className="profile-sidebar">
          <div 
            className="sidebar-header"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <h3>My Account</h3>
            <button 
              className="mobile-menu-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? '▲' : '▼'}
            </button>
          </div>
          <nav className={`sidebar-nav ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
            {sidebarItems.map(item => (
              <button
                key={item.id}
                className={`sidebar-item ${activeSection === item.id ? 'active' : ''} ${activeSection === item.id ? 'mobile-active-item' : 'mobile-hidden-item'}`}
                onClick={() => {
                  handleSidebarClick(item.id);
                  setIsMobileMenuOpen(false);
                }}
              >
                <span className="item-icon">{item.icon}</span>
                <span className="item-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="profile-main">
          {renderContent()}
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
        customActions={
          <button 
            className="success-btn primary"
            onClick={() => setShowSuccessModal(false)}
          >
            <i className="fas fa-check"></i>
            Got it!
          </button>
        }
      />
    </div>
  );
};

export default ProfilePage;